import { useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import InteractiveWaveform from "@/components/InteractiveWaveform";
import { cursos } from "@/mocks/cursos";
import SEO from "@/components/SEO";

export default function HomePage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const logoUrl = "https://pub-bc092def332d4fb180f02b510802da8e.r2.dev/LogoGP.png";

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "GodoyProd",
      url: siteUrl,
      description: "GodoyProd - A maior biblioteca de partituras gospel brasileiras em formato digital. Transcrições profissionais no Sibelius para músicos, igrejas e ministérios de louvor.",
      inLanguage: "pt-BR",
      publisher: {
        "@type": "Organization",
        name: "GodoyProd",
        url: siteUrl,
        logo: logoUrl,
        sameAs: [
          "https://www.instagram.com/eugoodoy",
          "https://www.youtube.com/@GodoyProd",
          "https://open.spotify.com/user/eugoodoy?si=8d3a4aea30714493",
        ],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "GodoyProd",
      url: siteUrl,
      logo: logoUrl,
      description: "Transcrição profissional de partituras gospel. Biblioteca digital, cursos de musicalização e comunidade de músicos.",
      sameAs: [
        "https://www.instagram.com/eugoodoy",
        "https://www.youtube.com/@GodoyProd",
        "https://open.spotify.com/user/eugoodoy?si=8d3a4aea30714493",
      ],
      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+55-15-99181-1212",
        contactType: "customer service",
        availableLanguage: ["Portuguese"],
      },
    },
  ];

  // Cursos em destaque: 1 de Leitura, 1 ProTools, 1 Melodyne, 1 Logic Pro
  const cursosDestaque = useMemo(() => {
    const leitura = cursos.find((c) => c.id === "leitura-partituras");
    const protools = cursos.find((c) => c.id === "protools-intro");
    const melodyne = cursos.find((c) => c.id === "melodyne-intro");
    const logicpro = cursos.find((c) => c.id === "logicpro-intro");
    return [leitura, protools, melodyne, logicpro].filter(Boolean) as typeof cursos;
  }, []);

  return (
    <>
      <SEO
        title="GodoyProd"
        description="GodoyProd - A maior biblioteca de partituras gospel brasileiras em formato digital. Transcrições profissionais no Sibelius, cursos de musicalização e comunidade de músicos."
        keywords="partituras gospel, transcrição sibelius, cifras gospel, partituras pdf, música gospel, pozzoli, bona, leitura de partituras, cursos de música, harmonia musical, produção musical"
        ogImage={logoUrl}
        schema={schema}
        canonicalPath="/"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        {/* Hero Section */}
        <section
          className="relative min-h-screen flex items-center justify-center overflow-hidden"
          style={{ width: '100vw', marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)' }}
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "url('https://readdy.ai/api/search-image?query=Dark%20elegant%20musical%20piano%20keys%20sheet%20music%20golden%20light%20abstract%20artistic%20background%20luxury%20professional%20studio%20atmosphere&width=3840&height=2160&seq=godoy-hero-bgv2&orientation=landscape')",
              backgroundSize: "cover",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              width: "100%",
              height: "100%",
              minWidth: "100vw",
            }}
          />
          <div className="absolute inset-0 bg-godoy-black/60" />
          <div className="absolute inset-0 bg-gradient-to-b from-godoy-black/50 via-transparent to-godoy-black" />
          <div className="absolute inset-0 w-full h-full" style={{ minWidth: '100vw' }}>
            <InteractiveWaveform />
          </div>

          <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 text-center">
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 md:w-32 md:h-32 animate-fade-in-up">
                <img
                  src="https://pub-bc092def332d4fb180f02b510802da8e.r2.dev/LogoGP.png"
                  alt="GodoyProd"
                  className="w-full h-full object-contain drop-shadow-2xl"
                />
              </div>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold text-godoy-cream mb-2 animate-fade-in-up tracking-[0.15em] uppercase" style={{ animationDelay: "0.2s", textShadow: "0 2px 40px rgba(201,168,76,0.3)" }}>
              GodoyProd
            </h1>
            <div className="w-24 h-[1px] bg-godoy-gold/60 mx-auto mb-6 animate-fade-in-up" style={{ animationDelay: "0.3s" }} />
            <p className="text-lg md:text-xl text-godoy-cream/70 max-w-2xl mx-auto mb-4 animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
              Transcrições de Partituras
            </p>
            <div className="animate-fade-in-up mb-10" style={{ animationDelay: "0.6s" }}>
              <p className="text-sm md:text-base text-godoy-cream font-medium max-w-xl mx-auto bg-godoy-black/50 backdrop-blur-sm rounded-lg px-6 py-4 leading-relaxed" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.7)" }}>
                A maior biblioteca de partituras gospel brasileiras em formato Digital.
                Partituras transcritas com excelência para músicos, igrejas e ministérios de louvor.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up" style={{ animationDelay: "0.8s" }}>
              <Link
                to="/biblioteca"
                className="px-8 py-3.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all whitespace-nowrap"
              >
                Explorar Biblioteca
              </Link>
              <Link
                to="/contato"
                className="px-8 py-3.5 border border-godoy-gold/50 text-godoy-gold font-semibold rounded-md hover:bg-godoy-gold/10 transition-all whitespace-nowrap"
              >
                Solicitar Transcrição
              </Link>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-godoy-gold/50 text-2xl" />
          </div>
        </section>

        {/* Serviços Section */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-godoy-gold text-sm font-semibold tracking-widest uppercase">Serviços</span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-godoy-cream mt-3">
                O que fazemos de melhor
              </h2>
              <p className="text-godoy-lightgray mt-4 max-w-2xl mx-auto">
                Transcrições com qualidade e precisão para músicos,
                igrejas e ministérios de louvor.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg p-8 hover:border-godoy-gold/40 transition-all group">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-godoy-gold/10 mb-6 group-hover:bg-godoy-gold/20 transition-all">
                  <i className="ri-file-music-line text-2xl text-godoy-gold" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-godoy-cream mb-3">
                  Transcrição de Partituras
                </h3>
                <p className="text-godoy-lightgray text-sm leading-relaxed">
                  Transcrição completa de qualquer música.
                  Partituras em PDF prontas para impressão ou leitura digital,
                  com qualidade profissional e notação precisa.
                </p>
              </div>

              <div className="bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg p-8 hover:border-godoy-gold/40 transition-all group">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-godoy-gold/10 mb-6 group-hover:bg-godoy-gold/20 transition-all">
                  <i className="ri-book-open-line text-2xl text-godoy-gold" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-godoy-cream mb-3">
                  Biblioteca Gospel
                </h3>
                <p className="text-godoy-lightgray text-sm leading-relaxed">
                  Centenas de partituras dos maiores artistas gospel brasileiros.
                  Catálogo organizado por artista, álbum e música para
                  encontrar exatamente o que você precisa.
                </p>
              </div>

              <div className="bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg p-8 hover:border-godoy-gold/40 transition-all group">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-godoy-gold/10 mb-6 group-hover:bg-godoy-gold/20 transition-all">
                  <i className="ri-graduation-cap-line text-2xl text-godoy-gold" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-godoy-cream mb-3">
                  Cursos
                </h3>
                <p className="text-godoy-lightgray text-sm leading-relaxed">
                  Cursos completos de teoria musical, leitura de partituras, harmonia e produção musical.
                  Do iniciante ao avançado, com preços a partir de R$ 9,90.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Cursos Destaque */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-godoy-gold text-sm font-semibold tracking-widest uppercase">Educação Musical</span>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-godoy-cream mt-3">
                Cursos Online
              </h2>
              <p className="text-godoy-lightgray mt-4 max-w-2xl mx-auto">
                Cursos completos de teoria musical, leitura de partituras, harmonia e produção musical.
                Do iniciante ao avançado, com preços a partir de R$ 9,90.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cursosDestaque.map((curso) => (
                <div
                  key={curso.id}
                  className="bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-lg overflow-hidden hover:border-godoy-gold/40 transition-all group"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={curso.imagem}
                      alt={curso.titulo}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-1 bg-godoy-gold/10 text-godoy-gold rounded">
                        {curso.nivel}
                      </span>
                      <span className="text-xs text-godoy-lightgray">
                        {curso.aulas} aulas
                      </span>
                    </div>
                    <h3 className="font-serif text-base font-semibold text-godoy-cream mb-2">
                      {curso.titulo}
                    </h3>
                    <p className="text-xs text-godoy-lightgray line-clamp-2 mb-4">
                      {curso.descricao}
                    </p>
                    <span className="inline-flex items-center text-xs text-godoy-gold font-medium">
                      <i className="ri-shopping-cart-line mr-1" />
                      R$ {curso.preco.toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link
                to="/cursos"
                className="inline-flex items-center gap-2 px-6 py-3 border border-godoy-gold/50 text-godoy-gold rounded-md hover:bg-godoy-gold/10 transition-all text-sm font-medium"
              >
                Ver todos os cursos
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="w-full px-6 md:px-10 lg:px-16 py-20 md:py-28 bg-godoy-dark">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-godoy-cream mb-6">
              Precisa de uma transcrição específica?
            </h2>
            <p className="text-godoy-lightgray text-lg mb-10 max-w-2xl mx-auto">
              Seja uma música específica, um arranjo personalizado ou uma
              transcrição para seu ministério de louvor. Entre em contato e
              solicite um orçamento sem compromisso.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contato"
                className="px-8 py-3.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all whitespace-nowrap"
              >
                Solicitar Transcrição
              </Link>
              <a
                href="https://wa.me/5515991811212"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3.5 border border-godoy-gold/50 text-godoy-gold font-semibold rounded-md hover:bg-godoy-gold/10 transition-all whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-whatsapp-line" />
                Falar no WhatsApp
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}